import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers, FaSearch, FaUserPlus, FaEnvelope, FaPhone,
  FaHeart, FaCalendarAlt, FaChevronDown, FaChevronUp,
  FaTrash, FaTimes, FaIdCard,
} from 'react-icons/fa';
import { Toaster } from 'react-hot-toast';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import { showToast } from '../utils/toast';
import api from '../../../services/api';

const SponsorsPage = () => {
  const { sponsors, brand, isOptionActive, fetchSponsors } = useProContext();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null); // { id, name } | null
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [togglingCardAdv, setTogglingCardAdv] = useState(new Set());

  const handleToggleCardAdv = async (userId, current) => {
    setTogglingCardAdv(prev => new Set(prev).add(userId));
    try {
      const token = localStorage.getItem('token');
      await api.patch(
        `/opticians/sponsors/${userId}/card-advantage`,
        { enabled: !current },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(current ? 'Carte avantage désactivée' : 'Carte avantage activée');
      if (fetchSponsors) await fetchSponsors();
    } catch (e) {
      showToast.error('Erreur lors de la mise à jour');
    }
    setTogglingCardAdv(prev => { const n = new Set(prev); n.delete(userId); return n; });
  };

  const handleRequestDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/opticians/sponsors/${deleteModal.id}`, { reason: deleteReason }, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success('Demande de suppression envoyée');
      setDeleteModal(null);
      setDeleteReason('');
      if (fetchSponsors) await fetchSponsors();
    } catch (e) {
      showToast.error('Erreur lors de la demande');
    }
    setDeleting(false);
  };

  const sorted = [...sponsors].sort((a, b) => (b.totalReferred || 0) - (a.totalReferred || 0));
  const filtered = sorted.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q) || (s.phone || '').includes(q);
  });

  const totalCA = sponsors.reduce((sum, s) => sum + (Number(s.totalCA) || 0), 0);
  const activeCount = sponsors.filter(s => {
    const last = s.last_login_at || s.lastLoginAt;
    return last && (new Date() - new Date(last)) < 7 * 24 * 3600000;
  }).length;

  const hasAnyOption = isOptionActive('family') || isOptionActive('appointment');

  const formatLastSeen = (s) => {
    const last = s.last_login_at || s.lastLoginAt;
    if (!last) {
      const created = s.created_at || s.createdAt;
      if (created) {
        const label = `Inscrit le ${new Date(created).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
        return { label, online: false };
      }
      return { label: '—', online: false };
    }
    const d = new Date(last);
    const now = new Date();
    const diffH = Math.floor((now - d) / 3600000);
    if (diffH < 1) return { label: 'En ligne', online: true };
    if (diffH < 24) return { label: `Il y a ${diffH}h`, online: false };
    if (diffH < 48) return { label: 'Hier', online: false };
    return { label: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }), online: false };
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Option badges for a user
  const getOptionBadges = (s) => {
    const badges = [];
    if (isOptionActive('family') && s.familyCount > 0) badges.push({ icon: FaHeart, label: `${s.familyCount} membre${s.familyCount > 1 ? 's' : ''} famille`, color: '#EC4899' });
    if (isOptionActive('appointment') && s.rdvCount > 0) badges.push({ icon: FaCalendarAlt, label: `${s.rdvCount} RDV`, color: '#3B82F6' });
    return badges;
  };

  return (
    <ProLayout title="Utilisateurs">
      <div className="p-5 space-y-4">

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">Total inscrits</p>
            <p className="text-xl font-bold text-gray-900">{sponsors.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">Actifs (7j)</p>
            <p className="text-xl font-bold text-green-600">{activeCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">Recommandations</p>
            <p className="text-xl font-bold text-gray-900">{sponsors.reduce((sum, s) => sum + (s.totalReferred || 0), 0)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
            <p className="text-[0.6875rem] text-gray-400">CA généré</p>
            <p className="text-xl font-bold" style={{ color: brand }}>{Math.ceil(totalCA)} €</p>
          </div>
        </div>

        {/* Search + Invite */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>
          <button onClick={() => navigate('/optician/invite')} className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl hover:opacity-90" style={{ backgroundColor: brand }}>
            <FaUserPlus className="w-3.5 h-3.5" /> Inviter
          </button>
        </div>

        {/* Users list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <div className="text-center py-12">
                <FaUsers className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">{search ? 'Aucun résultat' : 'Aucun utilisateur'}</p>
              </div>
            ) : filtered.map(s => {
              const { label: lastSeenLabel, online } = formatLastSeen(s);
              const badges = getOptionBadges(s);
              const isExpanded = expandedUser === s.id;

              return (
                <div key={s.id} className={`transition-colors ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                  {/* Main row */}
                  <div className="flex items-center px-4 py-3 gap-4 cursor-pointer" onClick={() => setExpandedUser(isExpanded ? null : s.id)}>
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[0.6875rem] font-bold flex-shrink-0" style={{ backgroundColor: brand }}>
                      {(s.first_name || '?')[0]}{(s.last_name || '?')[0]}
                    </div>

                    {/* Name + status */}
                    <div className="min-w-0 w-44">
                      <p className="text-sm font-semibold text-gray-900 truncate">{s.first_name} {s.last_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <span className={`text-[0.6875rem] ${online ? 'text-green-600' : 'text-gray-400'}`}>{lastSeenLabel}</span>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="min-w-0 w-48 hidden lg:block">
                      <p className="text-[0.6875rem] text-gray-500 truncate">{s.email || '—'}</p>
                      <p className="text-[0.6875rem] text-gray-400 mt-0.5">{s.phone || '—'}</p>
                    </div>

                    {/* Inscrit */}
                    <div className="w-28 hidden xl:block">
                      <p className="text-[0.6875rem] text-gray-400">{formatDate(s.created_at || s.createdAt)}</p>
                    </div>

                    {/* Option badges inline */}
                    {badges.length > 0 && (
                      <div className="flex items-center gap-1 flex-1 min-w-0">
                        {badges.slice(0, 3).map((b, i) => (
                          <span key={i} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[0.625rem] font-medium" style={{ backgroundColor: b.color + '12', color: b.color }}>
                            <b.icon className="w-2.5 h-2.5" /> {b.label}
                          </span>
                        ))}
                        {badges.length > 3 && <span className="text-[0.625rem] text-gray-400">+{badges.length - 3}</span>}
                      </div>
                    )}
                    {badges.length === 0 && <div className="flex-1" />}

                    {/* Stats */}
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-center w-12">
                        <p className="text-sm font-bold" style={{ color: s.totalReferred > 0 ? brand : '#D1D5DB' }}>{s.totalReferred || 0}</p>
                        <p className="text-[0.5625rem] text-gray-400">reco.</p>
                      </div>
                      <div className="text-center w-14">
                        <p className="text-sm font-bold text-gray-700">{Math.ceil(s.totalCA || 0)} €</p>
                        <p className="text-[0.5625rem] text-gray-400">CA</p>
                      </div>
                      <div className="text-center w-12">
                        <p className="text-sm font-medium text-gray-600">{s.sold || 0} €</p>
                        <p className="text-[0.5625rem] text-gray-400">solde</p>
                      </div>
                    </div>

                    {/* Expand arrow */}
                    {(hasAnyOption || true) && (
                      <div className="flex-shrink-0">
                        {isExpanded ? <FaChevronUp className="w-3 h-3 text-gray-300" /> : <FaChevronDown className="w-3 h-3 text-gray-300" />}
                      </div>
                    )}
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 ml-13 space-y-3">
                      {/* Contact info (always) */}
                      <div className="flex flex-wrap gap-4 text-[0.75rem]">
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FaEnvelope className="w-3 h-3 text-gray-300" /> {s.email || 'Pas d\'email'}
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FaPhone className="w-3 h-3 text-gray-300" /> {s.phone || 'Pas de téléphone'}
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <FaCalendarAlt className="w-3 h-3 text-gray-300" /> Inscrit le {formatDate(s.created_at || s.createdAt)}
                        </div>
                      </div>

                      {/* Option details */}
                      {badges.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {badges.map((b, i) => (
                            <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[0.6875rem] font-medium" style={{ backgroundColor: b.color + '10', color: b.color }}>
                              <b.icon className="w-3 h-3" /> {b.label}
                            </div>
                          ))}
                        </div>
                      )}

                      {badges.length === 0 && !s.totalReferred && (
                        <p className="text-[0.6875rem] text-gray-300">Cet utilisateur n'a pas encore d'activité</p>
                      )}

                      {/* Carte avantage toggle — only when the tenant option is active */}
                      {isOptionActive('card-advantage') && (
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 min-w-0">
                            <FaIdCard className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[0.75rem] font-semibold text-gray-700">Carte avantage</p>
                              <p className="text-[0.625rem] text-gray-400">Affichée sur l'app du client si activée</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleCardAdv(s.id, s.cardAdvantageEnabled)}
                            disabled={togglingCardAdv.has(s.id)}
                            aria-pressed={!!s.cardAdvantageEnabled}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${s.cardAdvantageEnabled ? '' : 'bg-gray-300'} ${togglingCardAdv.has(s.id) ? 'opacity-50 cursor-wait' : ''}`}
                            style={s.cardAdvantageEnabled ? { backgroundColor: brand } : {}}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${s.cardAdvantageEnabled ? 'translate-x-[1.125rem]' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      )}

                      {s.ask_delete && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg text-[0.6875rem] text-red-600 font-medium">
                          Suppression déjà demandée — en cours de traitement
                        </div>
                      )}

                      {/* Delete button */}
                      {!s.ask_delete && (
                        <div className="pt-2 border-t border-gray-100">
                          <button
                            onClick={() => setDeleteModal({ id: s.id, name: `${s.first_name} ${s.last_name}` })}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[0.6875rem] font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                          >
                            <FaTrash className="w-2.5 h-2.5" />
                            Demander la suppression
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Delete confirmation overlay */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setDeleteModal(null)}>
          <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <FaTrash className="w-4 h-4 text-red-500" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Demande de suppression</h3>
                  <p className="text-[0.6875rem] text-gray-500">{deleteModal.name}</p>
                </div>
                <button onClick={() => !deleting && setDeleteModal(null)} className="ml-auto p-1.5 hover:bg-gray-100 rounded-lg">
                  <FaTimes className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-[0.75rem] text-gray-600 leading-relaxed">
                Votre demande sera transmise à l'équipe Fideliz pour traitement. L'utilisateur sera informé et ses données seront supprimées sous 48h.
              </p>
              <div>
                <p className="text-[0.6875rem] font-semibold text-gray-500 mb-1.5">Raison (optionnel)</p>
                <textarea
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder="Ex: Le client n'est plus intéressé, il a demandé à ne plus recevoir de messages..."
                  rows={3}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => !deleting && setDeleteModal(null)}
                  className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200"
                >
                  Annuler
                </button>
                <button
                  onClick={handleRequestDelete}
                  disabled={deleting}
                  className="flex-1 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 disabled:opacity-40"
                >
                  {deleting ? 'Envoi...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </ProLayout>
  );
};

export default SponsorsPage;
